import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Slider,
  Chip,
  Avatar,
  CircularProgress,
} from '@mui/material';
import { CONTEXTS, CONTROLLERS } from 'figshelf-modules';

const TIERS = [
  { label: 'New', min: 0, max: 49, color: '#9e9e9e' },
  { label: 'Established', min: 50, max: 69, color: '#1565C0' },
  { label: 'Trusted', min: 70, max: 100, color: '#2e7d32' },
];

const getTier = (score) => TIERS.find((t) => score >= t.min && score <= t.max) || TIERS[0];

const MARKS = [
  { value: 0, label: '0' },
  { value: 49, label: '49' },
  { value: 50, label: '50' },
  { value: 69, label: '69' },
  { value: 70, label: '70' },
  { value: 100, label: '100' },
];

const TrustScoreModal = ({ open, onClose, user, onUpdated }) => {
  const api = useContext(CONTEXTS.APIContext);
  const [score, setScore] = useState(20);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      setScore(typeof user.trustScore === 'number' ? user.trustScore : 20);
      setError('');
    }
  }, [user]);

  if (!user) return null;

  const tier = getTier(score);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      const adminController = new CONTROLLERS.AdminController(api);
      await adminController.setTrustScore(user.id, score);
      onUpdated?.();
      onClose();
    } catch (err) {
      console.error('Failed to set trust score:', err);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Trust Score</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Avatar
            src={user.profile?.currentAvatar?.url || user.getAvatar?.()}
            alt={user.username}
            sx={{ width: 48, height: 48 }}
          />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {user.profile?.displayName || user.username}
            </Typography>
            <Typography variant="body2" color="text.secondary">@{user.username}</Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <Typography variant="body2" color="text.secondary">Current tier:</Typography>
          <Chip
            label={tier.label}
            size="small"
            sx={{ bgcolor: tier.color, color: '#fff', fontWeight: 600 }}
          />
          <Typography variant="h6" fontWeight={700} sx={{ ml: 'auto' }}>
            {score}
          </Typography>
        </Box>

        <Slider
          value={score}
          min={0}
          max={100}
          marks={MARKS}
          onChange={(_, v) => setScore(v)}
          sx={{
            color: tier.color,
            '& .MuiSlider-markLabel': { fontSize: '0.7rem' },
          }}
        />

        <Box sx={{ display: 'flex', gap: 1, mt: 3, justifyContent: 'center' }}>
          {TIERS.map((t) => (
            <Button
              key={t.label}
              variant="outlined"
              size="small"
              onClick={() => setScore(t.min)}
              sx={{ borderColor: t.color, color: t.color, '&:hover': { bgcolor: `${t.color}15` } }}
            >
              {t.label} ({t.min})
            </Button>
          ))}
        </Box>

        {error && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrustScoreModal;
